import React, { useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import PasswordPrompt from '../components/PasswordPrompt';
import { getApiUrl } from '../lib/getApiUrl';
import { useHeadlessAuth, silentTicketSignIn } from '../auth/headlessSignup';

export default function AdminCreate() {
  const { signOut } = useClerk();
  const { silentSignIn } = useHeadlessAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(''); // setup password (server secret, from gate)
  const [clerkPassword, setClerkPassword] = useState(''); // password for Clerk account
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [gated, setGated] = useState(true);
  const [showGate, setShowGate] = useState(true);
  // CB:BEGIN client-trust-admin-2fa – Client Trust: second factor (email code) when sign-in requires it
  const [showSecondFactor, setShowSecondFactor] = useState(false);
  const [secondFactorSignIn, setSecondFactorSignIn] = useState<any>(null);
  const [secondFactorCode, setSecondFactorCode] = useState('');
  // CB:END client-trust-admin-2fa

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || !firstName || !clerkPassword) return;
    setBusy(true);
    setMsg(null);
    try {
      const resp = await fetch(getApiUrl('/api/admin-create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-setup-password': password
        },
        body: JSON.stringify({ email, firstName, lastName, password: clerkPassword })
      });
      if (!resp.ok) {
        const t = await resp.text();
        setMsg(`Failed: ${resp.status} ${t}`);
        return;
      }
      await resp.json();
      // Sign out first so Clerk allows signing in as the new admin (avoids "You're already signed in").
      try {
        if (signOut) await signOut();
      } catch {}
      // Preferred: direct password sign-in via Clerk frontend SDK
      let signed = false;
      try {
        const clerk: any = (window as any).Clerk;
        if (clerk) {
          await clerk.load();
          const si = await clerk.client.signIn.create({ identifier: email, password: clerkPassword });
          if (si?.status === 'complete' && si?.createdSessionId) {
            await clerk.setActive({ session: si.createdSessionId });
            signed = true;
          } else if (si?.status === 'needs_second_factor') {
            const factors = si?.supportedSecondFactors || [];
            const emailCodeFactor = factors.find((f: { strategy: string }) => f.strategy === 'email_code');
            if (emailCodeFactor) {
              await si.prepareSecondFactor({ strategy: 'email_code', emailAddressId: emailCodeFactor.emailAddressId });
              setSecondFactorSignIn(si);
              setShowSecondFactor(true);
              setBusy(false);
              return;
            }
          }
        }
      } catch {}

      // Fallbacks: ticket and legacy helper
      if (!signed) {
        try { signed = await silentTicketSignIn(email, { firstName, lastName }); } catch {}
      }
      if (!signed) {
        try { signed = await silentSignIn(email, clerkPassword); } catch {}
      }

      try { localStorage.setItem('cb.usertype', 'admin'); } catch {}
      setMsg('✅ Admin created. Redirecting…');
      // Give Clerk time to settle the session then go to /admin regardless
      setTimeout(() => {
        window.location.href = '/admin';
      }, signed ? 800 : 1200);
      return;
    } catch (err: any) {
      setMsg(`Error: ${err?.message || 'Unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  // CB:BEGIN client-trust-admin-2fa-submit
  async function handleSecondFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!secondFactorSignIn || busy) return;
    setMsg(null);
    setBusy(true);
    try {
      const attempt = await secondFactorSignIn.attemptSecondFactor({ strategy: 'email_code', code: secondFactorCode });
      if (attempt?.status === 'complete' && attempt?.createdSessionId) {
        const clerk: any = (window as any).Clerk;
        if (clerk) await clerk.setActive({ session: attempt.createdSessionId });
        try { localStorage.setItem('cb.usertype', 'admin'); } catch {}
        setMsg('✅ Admin created. Redirecting…');
        setTimeout(() => { window.location.href = '/admin'; }, 800);
        return;
      }
      setMsg('Invalid or expired code. Please try again.');
    } catch (err: any) {
      setMsg(`Error: ${err?.message || 'Verification failed'}`);
    } finally {
      setBusy(false);
    }
  }
  // CB:END client-trust-admin-2fa-submit

  return (
    <div style={{ maxWidth: 520, margin: '60px auto', padding: 20 }}>
      {showGate && (
        <PasswordPrompt
          onSubmit={(pw) => {
            if (pw === 'CoreBids0728$$') {
              // Store the gate password to use as server header
              setPassword(pw);
              setGated(false);
              setShowGate(false);
              return true;
            }
            return false;
          }}
          onCancel={() => {
            setShowGate(false);
            // hide content if cancelled
            setGated(true);
          }}
        />
      )}
      {gated ? (
        <div style={{ textAlign: 'center', color: '#6b7280' }}>Protected</div>
      ) : showSecondFactor ? (
        <>
          <h2 style={{ marginTop: 0 }}>Verify your email</h2>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>We sent a verification code to your email. Enter it below.</p>
          <form onSubmit={handleSecondFactorSubmit} style={{ display: 'grid', gap: 12 }}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="Verification code"
              value={secondFactorCode}
              onChange={e => setSecondFactorCode(e.target.value)}
              className="form-control"
            />
            {msg && <div style={{ color: msg.startsWith('✅') ? '#065f46' : '#b91c1c' }}>{msg}</div>}
            <button type="submit" className="btn btn-primary" disabled={busy}>Verify and sign in</button>
          </form>
        </>
      ) : (
        <>
          <h2 style={{ marginTop: 0 }}>Admin Self-Service</h2>
          <p style={{ color: '#6b7280' }}>
            Enter the admin details. This will create a Clerk user and promote them in the database.
          </p>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label htmlFor="firstName" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>First Name</label>
                <input id="firstName" className="form-control" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
              </div>
              <div>
                <label htmlFor="lastName" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Last Name</label>
                <input id="lastName" className="form-control" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
              </div>
            </div>
            <div>
              <label htmlFor="email" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Admin Email</label>
              <input id="email" type="email" className="form-control" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div>
              <label htmlFor="clerkPw" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Admin Password (Clerk)</label>
              <input id="clerkPw" type="password" className="form-control" value={clerkPassword} onChange={e => setClerkPassword(e.target.value)} placeholder="Set admin login password" />
            </div>
            <button className="btn btn-primary" disabled={busy || !email || !firstName || !clerkPassword}>
              {busy ? 'Working…' : 'Create Admin'}
            </button>
          </form>
          {msg && <div style={{ marginTop: 12, color: msg.startsWith('✅') ? '#065f46' : '#b91c1c' }}>{msg}</div>}
        </>
      )}
    </div>
  );
}


